'use client';
import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import {
  getInvoices,
  getClients,
  getTimeLogs,
  getZones,
  formatCurrency,
  getSenderProfile,
  saveSenderProfile,
  SenderProfile,
} from '@/lib/storage';

// ─── Nav Groups ───────────────────────────────────────────────────────────────
const NAV_GROUPS = [
  {
    label: 'Main',
    items: [
      {
        href: '/hub', label: 'Hub', icon: (
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="2.5" fill="currentColor"/>
            <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
        )
      },
      {
        href: '/hub/live', label: 'Live', icon: (
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="2" fill="currentColor"/>
            <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.3" strokeDasharray="2 2" opacity="0.6"/>
            <path d="M8 2v2M8 12v2M2 8h2M12 8h2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
        )
      },
      {
        href: '/map', label: 'Map', icon: (
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <path d="M8 1.5C5.515 1.5 3.5 3.515 3.5 6c0 3.75 4.5 8.5 4.5 8.5S12.5 9.75 12.5 6c0-2.485-2.015-4.5-4.5-4.5z" stroke="currentColor" strokeWidth="1.4"/>
            <circle cx="8" cy="6" r="1.5" fill="currentColor"/>
          </svg>
        )
      },
      {
        href: '/dashboard', label: 'Dashboard', icon: (
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.9"/>
            <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.5"/>
            <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.5"/>
            <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.9"/>
          </svg>
        )
      },
    ]
  },
  {
    label: 'Finance',
    items: [
      {
        href: '/invoices', label: 'Invoices', icon: (
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <rect x="2" y="1" width="12" height="14" rx="2" stroke="currentColor" strokeWidth="1.4"/>
            <path d="M5 5h6M5 8h6M5 11h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        )
      },
      {
        href: '/clients', label: 'Clients', icon: (
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.4"/>
            <path d="M2 14c0-3.314 2.686-5 6-5s6 1.686 6 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        )
      },
      {
        href: '/templates', label: 'Templates', icon: (
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <rect x="1" y="1" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.4"/>
            <path d="M1 5h14" stroke="currentColor" strokeWidth="1.4"/>
            <path d="M5 9h6M5 12h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        )
      },
    ]
  },
  {
    label: 'Work',
    items: [
      {
        href: '/time-logs', label: 'Time Logs', icon: (
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4"/>
            <path d="M8 5v3l2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        )
      },
      {
        href: '/zones', label: 'Zones', icon: (
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
            <path d="M8 1.5C5.515 1.5 3.5 3.515 3.5 6c0 3.75 4.5 8.5 4.5 8.5S12.5 9.75 12.5 6c0-2.485-2.015-4.5-4.5-4.5z" stroke="currentColor" strokeWidth="1.4"/>
          </svg>
        )
      },
      {
        href: '/analytics', label: 'Analytics', icon: (
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <path d="M2 12l3-4 3 2 3-5 3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 14h12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.5"/>
          </svg>
        )
      },
      {
        href: '/alerts', label: 'Alerts', icon: (
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <path d="M8 1.5a5 5 0 0 1 5 5v2.5l1 2H2l1-2V6.5a5 5 0 0 1 5-5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
            <path d="M6.5 12.5a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        )
      },
    ]
  },
];

const ALL_NAV_ITEMS = NAV_GROUPS.flatMap(g => g.items);

interface SearchResult {
  id: string;
  type: 'invoice' | 'client' | 'timelog' | 'zone' | 'page';
  title: string;
  subtitle: string;
  href: string;
}

const SEARCH_HISTORY_KEY = 'nav_search_history';

function getSearchHistory(): string[] {
  if (typeof window === 'undefined') return [];
  try { return JSON.parse(localStorage.getItem(SEARCH_HISTORY_KEY) || '[]'); } catch { return []; }
}

function addSearchHistory(q: string) {
  if (!q.trim()) return;
  const h = getSearchHistory().filter(x => x !== q).slice(0, 7);
  localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify([q, ...h]));
}

// ─── Location-aware search ────────────────────────────────────────────────────
interface GeoSuggestion {
  id: string;
  name: string;
  address: string;
  distance?: string;
}

async function fetchNearbyPlaces(query: string, lat: number, lng: number): Promise<GeoSuggestion[]> {
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=3&countrycodes=au&lat=${lat}&lon=${lng}&bounded=0`;
    const res = await fetch(url, {
      headers: { 'Accept-Language': 'en', 'User-Agent': 'AppNav/1.0' },
    });
    if (!res.ok) return [];
    const data = await res.json();
    if (!Array.isArray(data)) return [];
    return data.slice(0, 3).map((item: Record<string, unknown>) => {
      const itemLat = parseFloat(String(item.lat));
      const itemLng = parseFloat(String(item.lon));
      const dLat = (itemLat - lat) * 111000;
      const dLng = (itemLng - lng) * 111000 * Math.cos(lat * Math.PI / 180);
      const distM = Math.sqrt(dLat * dLat + dLng * dLng);
      const distStr = distM < 1000 ? `${Math.round(distM)}m away` : `${(distM / 1000).toFixed(1)}km away`;
      return {
        id: `geo_${item.place_id ?? Math.random()}`,
        name: String(item.display_name ?? '').split(',')[0],
        address: String(item.display_name ?? ''),
        distance: distStr,
      };
    });
  } catch {
    return [];
  }
}

function useGlobalSearch(query: string, userLat: number | null, userLng: number | null): { results: SearchResult[]; geoSuggestions: GeoSuggestion[] } {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [geoSuggestions, setGeoSuggestions] = useState<GeoSuggestion[]>([]);
  const geoDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!query.trim() || query.length < 2) { setResults([]); setGeoSuggestions([]); return; }
    const q = query.toLowerCase();

    const pages = ALL_NAV_ITEMS.filter(p => p.label.toLowerCase().includes(q)).map(p => ({
      id: p.href, type: 'page' as const,
      title: p.label, subtitle: 'Navigate to page', href: p.href,
    }));

    const invoices = getInvoices().filter(inv =>
      inv.invoiceNumber?.toLowerCase().includes(q) ||
      inv.clientName?.toLowerCase().includes(q) ||
      inv.status?.toLowerCase().includes(q)
    ).slice(0, 4).map(inv => ({
      id: inv.id, type: 'invoice' as const,
      title: `${inv.invoiceNumber} — ${inv.clientName}`,
      subtitle: `${inv.status.toUpperCase()} · ${formatCurrency(inv.total)} · Due ${inv.dueDate}`,
      href: `/invoices/${inv.id}`,
    }));

    const clients = getClients().filter(c =>
      c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.company?.toLowerCase().includes(q)
    ).slice(0, 3).map(c => ({
      id: c.id, type: 'client' as const,
      title: c.name, subtitle: `${c.company ? c.company + ' · ' : ''}${c.email}`,
      href: `/clients`,
    }));

    const timeLogs = getTimeLogs().filter(l =>
      l.zoneName?.toLowerCase().includes(q) || l.category?.toLowerCase().includes(q)
    ).slice(0, 3).map(l => ({
      id: l.id, type: 'timelog' as const,
      title: l.zoneName || 'Unknown Zone',
      subtitle: `${l.category} · ${Math.floor(l.durationMinutes / 60)}h ${l.durationMinutes % 60}m`,
      href: `/time-logs`,
    }));

    const zones = getZones().filter(z =>
      z.name?.toLowerCase().includes(q) || z.type?.toLowerCase().includes(q)
    ).slice(0, 3).map(z => ({
      id: z.id, type: 'zone' as const,
      title: z.name, subtitle: `${z.type} zone · ${z.radiusMeters}m radius`,
      href: `/zones`,
    }));

    setResults([...pages, ...invoices, ...clients, ...timeLogs, ...zones]);

    // Location-aware geo suggestions
    if (userLat !== null && userLng !== null) {
      if (geoDebounceRef.current) clearTimeout(geoDebounceRef.current);
      geoDebounceRef.current = setTimeout(async () => {
        const geo = await fetchNearbyPlaces(query, userLat, userLng);
        setGeoSuggestions(geo);
      }, 400);
    } else {
      setGeoSuggestions([]);
    }
  }, [query, userLat, userLng]);

  return { results, geoSuggestions };
}

const TYPE_META: Record<string, { label: string; color: string; bg: string }> = {
  invoice: { label: 'Invoice', color: '#3B9EFF', bg: 'rgba(59,158,255,0.1)' },
  client: { label: 'Client', color: '#00E676', bg: 'rgba(0,230,118,0.1)' },
  timelog: { label: 'Log', color: '#F59E0B', bg: 'rgba(245,158,11,0.1)' },
  zone: { label: 'Zone', color: '#A78BFA', bg: 'rgba(167,139,250,0.1)' },
  page: { label: 'Page', color: '#6B7280', bg: 'rgba(107,114,128,0.1)' },
};

// ─── Route Update Banner ──────────────────────────────────────────────────────
interface RouteUpdateBannerProps {
  message: string;
  onDismiss: () => void;
}
function RouteUpdateBanner({ message, onDismiss }: RouteUpdateBannerProps) {
  return (
    <div
      className="fixed top-16 left-1/2 z-50 flex items-center gap-3 px-4 py-2.5 rounded-xl text-xs font-semibold shadow-lg"
      style={{
        transform: 'translateX(-50%)',
        background: 'rgba(245,158,11,0.15)',
        border: '1px solid rgba(245,158,11,0.35)',
        color: '#F59E0B',
        backdropFilter: 'blur(12px)',
        maxWidth: 360,
      }}
    >
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="shrink-0">
        <path d="M8 1.5C5.515 1.5 3.5 3.515 3.5 6c0 3.75 4.5 8.5 4.5 8.5S12.5 9.75 12.5 6c0-2.485-2.015-4.5-4.5-4.5z" stroke="#F59E0B" strokeWidth="1.4"/>
        <circle cx="8" cy="6" r="1.5" fill="#F59E0B"/>
      </svg>
      <span className="flex-1">{message}</span>
      <button onClick={onDismiss} className="ml-1 opacity-70 hover:opacity-100" style={{ minWidth: 20, minHeight: 20 }}>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
      </button>
    </div>
  );
}

export default function AppNav() {
  const pathname = usePathname();
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [batterySaver, setBatterySaver] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Profile panel state
  const [profileOpen, setProfileOpen] = useState(false);
  const [profile, setProfile] = useState<SenderProfile>({ name: '', email: '' });
  const [profileEdit, setProfileEdit] = useState<SenderProfile>({ name: '', email: '' });
  const [profileSaved, setProfileSaved] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  // User GPS location for search suggestions
  const [userLat, setUserLat] = useState<number | null>(null);
  const [userLng, setUserLng] = useState<number | null>(null);

  // Route update banner
  const [routeBanner, setRouteBanner] = useState<string | null>(null);
  const lastHeadingRef = useRef<number | null>(null);
  const routeWatchRef = useRef<number | null>(null);

  const { results, geoSuggestions } = useGlobalSearch(searchQuery, userLat, userLng);

  // Battery-aware rendering
  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const nav = navigator as Navigator & { getBattery?: () => Promise<{ level: number; charging: boolean; addEventListener: (e: string, cb: () => void) => void }> };
    if (!nav.getBattery) return;
    nav.getBattery().then((battery) => {
      const update = () => {
        const low = battery.level < 0.2 && !battery.charging;
        setBatterySaver(low);
        if (low) {
          document.documentElement.classList.add('battery-saver');
        } else {
          document.documentElement.classList.remove('battery-saver');
        }
      };
      update();
      battery.addEventListener('levelchange', update);
      battery.addEventListener('chargingchange', update);
    }).catch(() => {});
  }, []);

  // Load sender profile
  useEffect(() => {
    const p = getSenderProfile();
    setProfile(p);
    setProfileEdit(p);
  }, [profileOpen]);

  // GPS location for search suggestions
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        setUserLat(pos.coords.latitude);
        setUserLng(pos.coords.longitude);
      },
      () => {},
      { enableHighAccuracy: false, maximumAge: 30000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // Auto route update: watch heading on bike/race pages
  useEffect(() => {
    const isNavPage = pathname === '/hub/bike-nav' || pathname === '/hub/ebike-race';
    if (!isNavPage) {
      if (routeWatchRef.current !== null) {
        navigator.geolocation.clearWatch(routeWatchRef.current);
        routeWatchRef.current = null;
      }
      lastHeadingRef.current = null;
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;

    routeWatchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const heading = pos.coords.heading;
        if (heading === null || isNaN(heading)) return;
        if (lastHeadingRef.current === null) {
          lastHeadingRef.current = heading;
          return;
        }
        const diff = Math.abs(heading - lastHeadingRef.current);
        const normalizedDiff = diff > 180 ? 360 - diff : diff;
        // If heading changed by more than 45° — suggest route update
        if (normalizedDiff > 45) {
          lastHeadingRef.current = heading;
          setRouteBanner('Route updated — heading changed. Recalculating…');
          setTimeout(() => setRouteBanner(null), 5000);
        }
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 2000 }
    );

    return () => {
      if (routeWatchRef.current !== null) {
        navigator.geolocation.clearWatch(routeWatchRef.current);
        routeWatchRef.current = null;
      }
    };
  }, [pathname]);

  useEffect(() => {
    const invoices = getInvoices();
    const timeLogs = getTimeLogs();
    const overdue = invoices.filter(i => i.status === 'overdue').length;
    const pending = timeLogs.filter(l => l.status === 'to_be_confirmed').length;
    setAlertCount(overdue + pending);
  }, [pathname]);

  useEffect(() => {
    setSearchHistory(getSearchHistory());
  }, [searchOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [pathname]);

  function handleResultClick(href: string, title?: string) {
    if (title) addSearchHistory(title);
    setSearchOpen(false);
    setSearchQuery('');
    setSelectedIdx(-1);
    router.push(href);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { setSearchOpen(false); setSearchQuery(''); setSelectedIdx(-1); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, -1)); }
    if (e.key === 'Enter' && selectedIdx >= 0 && results[selectedIdx]) {
      handleResultClick(results[selectedIdx].href, results[selectedIdx].title);
    }
  }

  function handleProfileSave() {
    saveSenderProfile(profileEdit);
    setProfile(profileEdit);
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 2000);
  }

  const showDropdown = searchOpen && (results.length > 0 || geoSuggestions.length > 0 || (searchQuery.length === 0 && searchHistory.length > 0));
  const isActive = (href: string) => href === '/hub' ? pathname === '/hub' : pathname.startsWith(href);

  const profileInitials = profile.name
    ? profile.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <>
      {/* Route update banner */}
      {routeBanner && (
        <RouteUpdateBanner message={routeBanner} onDismiss={() => setRouteBanner(null)} />
      )}

      <nav className="fixed top-0 left-0 right-0 z-50" style={{ background: 'rgba(15,19,24,0.96)', backdropFilter: 'blur(24px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center gap-3">
          {/* Logo */}
          <Link href="/hub" className="flex items-center gap-2 shrink-0 mr-1">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--green)' }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 10L5 4l3 4 2-2 2 4" stroke="#0F1318" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="font-semibold text-sm hidden sm:block" style={{ color: 'var(--paper)', letterSpacing: '-0.01em' }}>InvoiceKit</span>
          </Link>

          {/* Desktop Nav — grouped */}
          <div className="hidden lg:flex items-center gap-1 flex-1">
            {ALL_NAV_ITEMS.map(item => (
              <Link
                key={item.href}
                href={item.href}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${isActive(item.href) ? 'nav-active' : ''}`}
                style={{
                  color: isActive(item.href) ? 'var(--green)' : 'var(--chrome-dim)',
                  textDecoration: 'none',
                }}
              >
                {item.icon}
                {item.label}
              </Link>
            ))}
          </div>

          {/* Global Search */}
          <div ref={searchRef} className="relative flex-1 lg:flex-none lg:w-72">
            <div className="relative flex items-center">
              <svg className="absolute left-3 pointer-events-none" width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ color: 'var(--chrome-dim)' }}>
                <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10 10l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setSearchOpen(true); setSelectedIdx(-1); }}
                onFocus={() => setSearchOpen(true)}
                onKeyDown={handleKeyDown}
                placeholder={userLat ? 'Search near you…' : 'Search everything…'}
                className="w-full pl-8 pr-8 py-1.5 rounded-lg text-xs outline-none transition-all"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: `1px solid ${searchOpen ? 'rgba(0,230,118,0.25)' : 'rgba(255,255,255,0.07)'}`,
                  color: 'var(--paper)',
                }}
              />
              {searchQuery ? (
                <button onClick={() => { setSearchQuery(''); setSearchOpen(false); setSelectedIdx(-1); }} className="absolute right-2.5 flex items-center justify-center w-4 h-4 rounded-full" style={{ background: 'rgba(168,176,186,0.15)', color: 'var(--chrome)' }}>
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 1l6 6M7 1L1 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </button>
              ) : (
                <span className="absolute right-2.5 text-xs font-mono flex items-center gap-1" style={{ color: 'var(--chrome-dim)', fontSize: '0.65rem' }}>
                  {userLat && (
                    <span title="Location-aware search active" style={{ color: '#00E676' }}>
                      <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                        <circle cx="5" cy="5" r="3.5" stroke="#00E676" strokeWidth="1.2"/>
                        <circle cx="5" cy="5" r="1.5" fill="#00E676"/>
                      </svg>
                    </span>
                  )}
                  ⌘K
                </span>
              )}
            </div>

            {/* Search Dropdown */}
            {showDropdown && (
              <div className="search-dropdown absolute top-full mt-2 left-0 right-0 z-50" style={{ minWidth: 320 }}>
                {searchQuery.length === 0 && searchHistory.length > 0 && (
                  <div>
                    <div className="px-3 py-2 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--chrome-dim)' }}>Recent Searches</span>
                      <button onClick={() => { localStorage.removeItem(SEARCH_HISTORY_KEY); setSearchHistory([]); }} className="text-xs" style={{ color: 'var(--chrome-dim)' }}>Clear</button>
                    </div>
                    {searchHistory.map((h, i) => (
                      <button key={i} onClick={() => { setSearchQuery(h); }} className="search-result-item w-full flex items-center gap-2.5 px-3 py-2.5 text-left">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ color: 'var(--chrome-dim)', flexShrink: 0 }}>
                          <path d="M6 1a5 5 0 1 0 0 10A5 5 0 0 0 6 1zM6 3v3l2 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                        </svg>
                        <span className="text-xs" style={{ color: 'var(--chrome)' }}>{h}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Location-aware geo suggestions */}
                {geoSuggestions.length > 0 && (
                  <div>
                    <div className="px-3 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <span className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5" style={{ color: '#00E676' }}>
                        <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                          <circle cx="5" cy="5" r="3.5" stroke="#00E676" strokeWidth="1.2"/>
                          <circle cx="5" cy="5" r="1.5" fill="#00E676"/>
                        </svg>
                        Near You
                      </span>
                    </div>
                    {geoSuggestions.map((g) => (
                      <button
                        key={g.id}
                        onClick={() => { handleResultClick('/map', g.name); }}
                        className="search-result-item w-full flex items-center gap-3 px-3 py-2.5 text-left"
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                      >
                        <span className="text-xs font-semibold px-1.5 py-0.5 rounded-md flex-shrink-0" style={{ color: '#00E676', background: 'rgba(0,230,118,0.1)', minWidth: 44, textAlign: 'center' }}>Nearby</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate" style={{ color: 'var(--paper)' }}>{g.name}</p>
                          <p className="text-xs truncate" style={{ color: 'var(--chrome-dim)' }}>{g.distance} · {g.address.split(',').slice(1, 3).join(',').trim()}</p>
                        </div>
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ color: 'var(--chrome-dim)', flexShrink: 0 }}>
                          <path d="M2 5h6M5 2l3 3-3 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    ))}
                  </div>
                )}

                {results.length > 0 && (
                  <div>
                    {results.map((r, i) => {
                      const meta = TYPE_META[r.type];
                      return (
                        <button
                          key={r.id}
                          onClick={() => handleResultClick(r.href, r.title)}
                          className={`search-result-item w-full flex items-center gap-3 px-3 py-2.5 text-left ${selectedIdx === i ? 'bg-white/[0.05]' : ''}`}
                          style={{ borderBottom: i < results.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}
                        >
                          <span className="text-xs font-semibold px-1.5 py-0.5 rounded-md flex-shrink-0" style={{ color: meta.color, background: meta.bg, minWidth: 44, textAlign: 'center' }}>{meta.label}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate" style={{ color: 'var(--paper)' }}>{r.title}</p>
                            <p className="text-xs truncate" style={{ color: 'var(--chrome-dim)' }}>{r.subtitle}</p>
                          </div>
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ color: 'var(--chrome-dim)', flexShrink: 0 }}>
                            <path d="M2 5h6M5 2l3 3-3 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      );
                    })}
                  </div>
                )}
                {searchQuery.length >= 2 && results.length === 0 && geoSuggestions.length === 0 && (
                  <div className="px-4 py-6 text-center">
                    <p className="text-xs" style={{ color: 'var(--chrome-dim)' }}>No results for &quot;{searchQuery}&quot;</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-1.5 shrink-0">
            {/* Alerts */}
            <Link href="/alerts" className="relative flex items-center justify-center w-8 h-8 rounded-lg transition-all" style={{ color: alertCount > 0 ? '#F87171' : 'var(--chrome-dim)', background: alertCount > 0 ? 'rgba(248,113,113,0.08)' : 'transparent' }}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path d="M8 1.5a5 5 0 0 1 5 5v2.5l1 2H2l1-2V6.5a5 5 0 0 1 5-5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
                <path d="M6.5 12.5a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
              {alertCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-xs font-bold flex items-center justify-center" style={{ background: '#F87171', color: '#0F1318', fontSize: '0.6rem' }}>{alertCount}</span>
              )}
            </Link>

            {/* New Invoice */}
            <Link href="/invoices/new" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all" style={{ background: 'var(--green)', color: '#0F1318' }}>
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 1v9M1 5.5h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              Invoice
            </Link>

            {/* Settings */}
            <Link href="/hub/settings" className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all ${isActive('/hub/settings') ? 'nav-active' : ''}`} style={{ color: isActive('/hub/settings') ? 'var(--green)' : 'var(--chrome-dim)' }}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M8 1.5v1.5M8 13v1.5M1.5 8H3M13 8h1.5M3.4 3.4l1.1 1.1M11.5 11.5l1.1 1.1M3.4 12.6l1.1-1.1M11.5 4.5l1.1-1.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
            </Link>

            {/* Profile Avatar Button */}
            <div ref={profileRef} className="relative">
              <button
                onClick={() => setProfileOpen(o => !o)}
                className="flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold transition-all"
                style={{
                  background: profileOpen ? 'var(--green)' : 'rgba(0,230,118,0.15)',
                  color: profileOpen ? '#0F1318' : 'var(--green)',
                  border: `1.5px solid ${profileOpen ? 'var(--green)' : 'rgba(0,230,118,0.3)'}`,
                  fontSize: '0.6rem',
                }}
                title="Sender Profile"
              >
                {profileInitials}
              </button>

              {/* Profile Flyout */}
              {profileOpen && (
                <div
                  className="absolute right-0 top-full mt-2 z-50 rounded-xl overflow-hidden"
                  style={{
                    width: 280,
                    background: 'rgba(15,19,24,0.98)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    boxShadow: '0 16px 48px rgba(0,0,0,0.7)',
                    backdropFilter: 'blur(24px)',
                  }}
                >
                  {/* Header */}
                  <div className="px-4 py-3 flex items-center gap-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: 'rgba(0,230,118,0.15)', color: 'var(--green)', border: '1.5px solid rgba(0,230,118,0.3)', fontSize: '0.65rem' }}>
                      {profileInitials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate" style={{ color: 'var(--paper)' }}>{profile.name || 'No name set'}</p>
                      <p className="text-xs truncate" style={{ color: 'var(--chrome-dim)' }}>{profile.email || 'No email set'}</p>
                    </div>
                  </div>

                  {/* Edit fields */}
                  <div className="px-4 py-3 flex flex-col gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--chrome-dim)' }}>Sender Profile</p>
                    {[
                      { key: 'name', label: 'Name', placeholder: 'Your name' },
                      { key: 'email', label: 'Email', placeholder: 'your@email.com' },
                      { key: 'company', label: 'Company', placeholder: 'Company (optional)' },
                      { key: 'abn', label: 'ABN', placeholder: 'ABN number' },
                    ].map(field => (
                      <div key={field.key}>
                        <label className="text-xs mb-0.5 block" style={{ color: 'var(--chrome-dim)' }}>{field.label}</label>
                        <input
                          type="text"
                          value={(profileEdit as Record<string, string>)[field.key] || ''}
                          onChange={e => setProfileEdit(prev => ({ ...prev, [field.key]: e.target.value }))}
                          placeholder={field.placeholder}
                          className="w-full px-2.5 py-1.5 rounded-lg text-xs outline-none"
                          style={{
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            color: 'var(--paper)',
                          }}
                        />
                      </div>
                    ))}
                    <button
                      onClick={handleProfileSave}
                      className="mt-1 w-full py-2 rounded-lg text-xs font-semibold transition-all"
                      style={{ background: profileSaved ? 'rgba(0,230,118,0.15)' : 'var(--green)', color: profileSaved ? 'var(--green)' : '#0F1318' }}
                    >
                      {profileSaved ? '✓ Saved' : 'Save Profile'}
                    </button>
                  </div>

                  {/* Quick links */}
                  <div className="px-4 py-2 flex flex-col gap-0.5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--chrome-dim)' }}>Quick Links</p>
                    <Link
                      href="/clients"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-2.5 px-2 py-2 rounded-lg text-xs font-medium transition-all"
                      style={{ color: 'var(--chrome)', textDecoration: 'none' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.4"/>
                        <path d="M2 14c0-3.314 2.686-5 6-5s6 1.686 6 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                      </svg>
                      Clients
                    </Link>
                    <Link
                      href="/invoices"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-2.5 px-2 py-2 rounded-lg text-xs font-medium transition-all"
                      style={{ color: 'var(--chrome)', textDecoration: 'none' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                        <rect x="2" y="1" width="12" height="14" rx="2" stroke="currentColor" strokeWidth="1.4"/>
                        <path d="M5 5h6M5 8h6M5 11h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                      </svg>
                      Invoices
                    </Link>
                    <Link
                      href="/hub/settings"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-2.5 px-2 py-2 rounded-lg text-xs font-medium transition-all"
                      style={{ color: 'var(--chrome)', textDecoration: 'none' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
                        <path d="M8 1.5v1.5M8 13v1.5M1.5 8H3M13 8h1.5M3.4 3.4l1.1 1.1M11.5 11.5l1.1 1.1M3.4 12.6l1.1-1.1M11.5 4.5l1.1-1.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                      </svg>
                      Settings
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile menu toggle */}
            <button onClick={() => setMobileOpen(o => !o)} className="lg:hidden flex items-center justify-center w-8 h-8 rounded-lg" style={{ color: 'var(--chrome-dim)', background: mobileOpen ? 'rgba(255,255,255,0.06)' : 'transparent' }}>
              {mobileOpen ? (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              ) : (
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M2 4h11M2 7.5h11M2 11h11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t" style={{ background: 'rgba(15,19,24,0.98)', borderColor: 'rgba(255,255,255,0.06)' }}>
            {NAV_GROUPS.map(group => (
              <div key={group.label} className="px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: 'var(--chrome-dim)' }}>{group.label}</p>
                <div className="grid grid-cols-2 gap-1">
                  {group.items.map(item => (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${isActive(item.href) ? 'nav-active' : ''}`}
                      style={{ color: isActive(item.href) ? 'var(--green)' : 'var(--chrome)', textDecoration: 'none' }}
                    >
                      {item.icon}
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}

            {/* Mobile Profile & Clients section */}
            <div className="px-4 py-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: 'var(--chrome-dim)' }}>Account</p>
              <div className="grid grid-cols-2 gap-1">
                <button
                  onClick={() => { setMobileOpen(false); setProfileOpen(true); }}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium transition-all"
                  style={{ color: 'var(--chrome)', background: 'transparent' }}
                >
                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: 'rgba(0,230,118,0.15)', color: 'var(--green)', fontSize: '0.55rem' }}>
                    {profileInitials}
                  </div>
                  Sender Profile
                </button>
                <Link
                  href="/clients"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium transition-all"
                  style={{ color: isActive('/clients') ? 'var(--green)' : 'var(--chrome)', textDecoration: 'none' }}
                >
                  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.4"/>
                    <path d="M2 14c0-3.314 2.686-5 6-5s6 1.686 6 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                  </svg>
                  Clients
                </Link>
              </div>
            </div>

            <div className="px-4 py-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
              <Link href="/invoices/new" className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-xs font-semibold" style={{ background: 'var(--green)', color: '#0F1318' }}>
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 1v9M1 5.5h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                New Invoice
              </Link>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
